import React, { useCallback, useEffect, useRef, useState } from "react";
import { ArrowLeft, X } from "lucide-react";
import Input from "src/components/app/Input";
import { useNavigate } from "react-router-dom";
import { useDebounce } from "src/hooks/useDebounce";
import InfinitePaginatedList from "../Pagination/InfinitePaginatedList";

type GenericSearchScreenProps<T, S = any> = {
  title?: string;
  placeholder?: string;
  fetchResults?: (
    searchTerm: string,
    page: number
  ) => Promise<{ items: T[]; hasMore: boolean }>;
  renderItem: (item: T, index: number) => React.ReactNode;
  items?: T[];
  emptyMessage?: React.ReactNode;
  inputDisabled?: boolean;
  onInputChange?: (value: string) => void;
  searchValue?: string;
  onBackClick?: () => void;
  showSearchedScreen?: (
    searchTerm: string,
    setShowSearchedScreen: (show: boolean) => void
  ) => React.ReactNode;
  supportingItems?: S[];
};

function GenericSearchScreen<T, S = any>({
  title = "Search",
  placeholder = "Search...",
  fetchResults,
  renderItem,
  emptyMessage = (
    <div className="py-8 flex justify-center">
      <p className="text-gray-500 text-sm text-center">No Results Found</p>
    </div>
  ),
  inputDisabled = false,
  onInputChange,
  searchValue,
  items,
  onBackClick,
  showSearchedScreen,
  supportingItems,
}: GenericSearchScreenProps<T, S>) {
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearchQuery = useDebounce(searchValue || searchQuery, 500);
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const timeout = setTimeout(() => {
      inputRef.current?.focus();
    }, 50);
    return () => clearTimeout(timeout);
  }, []);

  const fetchData = useCallback(
    async (page: number) => {
      // Only call API if the debounced query has content
      if (!debouncedSearchQuery || !fetchResults) {
        return { items: [], hasMore: false };
      }

      return await fetchResults(debouncedSearchQuery, page);
    },
    [debouncedSearchQuery, fetchResults]
  );

  return (
    <div className="bg-white h-screen w-full max-w-lg mx-auto flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center gap-2 mb-3">
          <ArrowLeft
            className="w-5 h-5 text-gray-700 shrink-0 cursor-pointer"
            onClick={() => (onBackClick ? onBackClick() : navigate(-1))}
          />
          <div className="w-full">
            <Input
              name="search"
              type="text"
              placeholder={placeholder}
              fullWidth
              value={searchValue || searchQuery}
              onChange={(e) => {
                const value = e.target.value;
                onInputChange ? onInputChange(value) : setSearchQuery(value);
              }}
              endAdornment={
                (searchValue || searchQuery) && (
                  <X
                    className="w-5 h-5 text-gray-400 ml-2 cursor-pointer"
                    onClick={() =>
                      onInputChange ? onInputChange("") : setSearchQuery("")
                    }
                  />
                )
              }
              disabled={inputDisabled}
              inputRef={inputRef}
            />
          </div>
        </div>
        <h2 className="text-sm font-semibold text-gray-800">
          {searchValue || searchQuery ? "Search Results" : "Recent Searches"}
        </h2>
      </div>

      {/* Scrollable Area */}
      <div className="flex-1 overflow-y-auto px-4 pt-2">
        {fetchResults && debouncedSearchQuery ? (
          <InfinitePaginatedList
            key={debouncedSearchQuery} // Forces reset only after debounce
            containerHeight="calc(100vh - 120px)"
            fetchData={fetchData}
            renderItem={renderItem}
            emptyMessage={emptyMessage}
          />
        ) : items?.length ? (
          items.map((item, index) => renderItem(item, index))
        ) : (
          emptyMessage
        )}
      </div>
    </div>
  );
}

export default GenericSearchScreen;
